import { logger } from '@/utils/logger';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { parse } from 'yaml';

import type {
  CreatePipelineConfigurationVersionBody,
  CreatePipelineConfigurationVersionResponse,
  GetPipelineConfigurationVersionByIdResponse,
  GetPipelineConfigurationVersionsRequest,
  GetPipelineConfigurationVersionsResponse,
  UpdatePipelineConfigurationVersionBody,
  UpdatePipelineConfigurationVersionResponse,
} from '@mai/platform-api/src/routes/pipeline-configuration-versions.schema';
import type {
  CreatePipelineExecutionBody,
  CreatePipelineExecutionResponse,
  GetPipelineExecutionByIdResponse,
  GetPipelineExecutionOutputResponse,
  GetPipelineExecutionsRequest,
  GetPipelineExecutionsResponse,
} from '@mai/platform-api/src/routes/pipeline-executions.schema';
import type {
  CreatePipelineBody,
  CreatePipelineResponse,
  GetPipelineByIdResponse,
  GetPipelinesRequest,
  GetPipelinesResponse,
  UpdatePipelineBody,
  UpdatePipelineResponse,
} from '@mai/platform-api/src/routes/pipelines.schema';
import type { PipelineExecutionProgressData } from '@mai/types';

import { type QueryOptions, apiClient } from './axios';
import { useWebsocketPubSub } from './websockets';

// Export PipelineConfigurationVersion type from types package
export type { PipelineConfigurationVersion } from '@mai/types';
export type { PipelineExecutionProgressData } from '@mai/types';
export type { GetPipelineExecutionOutputResponse } from '@mai/platform-api/src/routes/pipeline-executions.schema';

// API functions for pipelines
export const fetchPipelines = async (
  params?: GetPipelinesRequest,
): Promise<GetPipelinesResponse> => {
  const response = await apiClient.get('/pipelines', { params });
  return response.data;
};

export const fetchPipelinesByTeamId = async (
  teamId: string,
): Promise<GetPipelinesResponse> => {
  return fetchPipelines({ teamIds: [teamId] });
};

export const fetchPipelineById = async (
  pipelineId: string,
): Promise<GetPipelineByIdResponse> => {
  const response = await apiClient.get(`/pipelines/${pipelineId}`);
  return response.data;
};

export const createPipeline = async (
  data: CreatePipelineBody,
): Promise<CreatePipelineResponse> => {
  const response = await apiClient.post('/pipelines', data);
  return response.data;
};

export const updatePipeline = async (
  pipelineId: string,
  data: UpdatePipelineBody,
): Promise<UpdatePipelineResponse> => {
  const response = await apiClient.patch(`/pipelines/${pipelineId}`, data);
  return response.data;
};

export const deletePipeline = async (pipelineId: string): Promise<void> => {
  await apiClient.delete(`/pipelines/${pipelineId}`);
};

// API functions for pipeline configuration versions
export const fetchPipelineConfigurationVersions = async (
  params?: GetPipelineConfigurationVersionsRequest,
): Promise<GetPipelineConfigurationVersionsResponse> => {
  const response = await apiClient.get('/pipeline-configuration-versions', {
    params,
  });
  return response.data;
};

export const fetchPipelineConfigurationVersionsByPipelineId = async (
  pipelineId: string,
): Promise<GetPipelineConfigurationVersionsResponse> => {
  return fetchPipelineConfigurationVersions({ pipelineIds: [pipelineId] });
};

// API functions for pipeline executions
export const fetchPipelineExecutions = async (
  params?: GetPipelineExecutionsRequest,
): Promise<GetPipelineExecutionsResponse> => {
  const response = await apiClient.get('/pipeline-executions', { params });
  return response.data;
};

export const fetchPipelineExecutionsByPipelineId = async (
  pipelineId: string,
): Promise<GetPipelineExecutionsResponse> => {
  return fetchPipelineExecutions({
    pipelineIds: [pipelineId],
    orderBy: 'createdAt',
    orderDirection: 'desc',
    pageSize: 20, // Get more recent runs to ensure we have enough for the dropdown
  });
};

// API functions for configuration management
export const createPipelineConfigurationVersion = async (
  data: CreatePipelineConfigurationVersionBody,
): Promise<CreatePipelineConfigurationVersionResponse> => {
  const requestBody = {
    pipelineId: data.pipelineId,
    configuration: data.configuration,
    status: data.status || 'draft',
  };

  const response = await apiClient.post(
    '/pipeline-configuration-versions',
    requestBody,
  );
  return response.data;
};

export const updatePipelineConfigurationVersion = async (
  pipelineConfigurationVersionId: string,
  data: UpdatePipelineConfigurationVersionBody,
): Promise<UpdatePipelineConfigurationVersionResponse> => {
  const response = await apiClient.patch(
    `/pipeline-configuration-versions/${pipelineConfigurationVersionId}`,
    data,
  );
  return response.data;
};

export const deletePipelineConfigurationVersion = async (
  pipelineConfigurationVersionId: string,
): Promise<void> => {
  await apiClient.delete(
    `/pipeline-configuration-versions/${pipelineConfigurationVersionId}`,
  );
};

export const fetchPipelineExecutionById = async (
  pipelineExecutionId: string,
): Promise<GetPipelineExecutionByIdResponse> => {
  const response = await apiClient.get(
    `/pipeline-executions/${pipelineExecutionId}`,
  );
  return response.data;
};

export const fetchPipelineConfigurationVersionById = async (
  pipelineConfigurationVersionId: string,
): Promise<GetPipelineConfigurationVersionByIdResponse> => {
  const response = await apiClient.get(
    `/pipeline-configuration-versions/${pipelineConfigurationVersionId}`,
  );
  return response.data;
};

export const createPipelineExecution = async (
  data: CreatePipelineExecutionBody,
): Promise<CreatePipelineExecutionResponse> => {
  const response = await apiClient.post('/pipeline-executions', data);
  return response.data;
};

export const fetchPipelineExecutionOutput = async (
  pipelineExecutionId: string,
): Promise<GetPipelineExecutionOutputResponse> => {
  const response = await apiClient.get(
    `/pipeline-executions/${pipelineExecutionId}/output`,
  );
  return response.data;
};

// API function for pipeline validation
export interface BlockTypeError {
  type: 'block_type_error';
  blockId: string;
  blockType: string;
  errorType: string;
  message: string;
  portName?: string | null;
  sourceBlockId?: string | null;
  sourcePort?: string | null;
}

export interface ConnectionError {
  type: 'connection_error';
  connectionId: string;
  sourceBlockId: string;
  sourcePort: string;
  targetBlockId: string;
  targetPort: string;
  errorType: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: (BlockTypeError | ConnectionError)[];
  warnings: ConnectionError[];
  inputSchema?: Record<string, unknown> | null;
  outputSchema?: Record<string, unknown> | null;
}

// Legacy interface for backward compatibility
export interface ValidationError {
  message: string;
  path?: string;
  code?: string;
}

export const validatePipelineConfiguration = async (
  yamlContent: string,
): Promise<ValidationResult> => {
  try {
    // Parse YAML to JSON
    const configuration = parse(yamlContent);

    // Call validation API
    const response = await apiClient.post(
      '/pipeline-configuration-versions/validate',
      {
        configuration,
      },
    );

    // Normalize the response to match our expected format
    const apiResponse = response.data;

    return {
      valid: apiResponse.valid,
      errors: apiResponse.errors || [],
      warnings: apiResponse.warnings || [],
      inputSchema: apiResponse.inputSchema,
      outputSchema: apiResponse.outputSchema,
    };
  } catch (parseError) {
    // Handle YAML parsing errors
    if (parseError instanceof Error && parseError.name === 'YAMLParseError') {
      return {
        valid: false,
        errors: [
          {
            type: 'block_type_error',
            blockId: 'yaml_parser',
            blockType: 'parser',
            errorType: 'yaml_parse_error',
            message: `YAML Parse Error: ${parseError.message}`,
            portName: null,
            sourceBlockId: null,
            sourcePort: null,
          },
        ],
        warnings: [],
      };
    }

    // Re-throw other errors for React Query to handle
    throw parseError;
  }
};

// React Query hooks for pipelines
export const usePipelines = (
  params?: GetPipelinesRequest,
  options?: QueryOptions<GetPipelinesResponse>,
) => {
  return useQuery({
    queryKey: ['pipelines', params],
    queryFn: () => fetchPipelines(params),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const usePipelinesByTeamId = (
  teamId: string | null,
  options?: QueryOptions<GetPipelinesResponse>,
) => {
  return useQuery({
    queryKey: ['pipelines', 'by-team', teamId],
    queryFn: () => fetchPipelinesByTeamId(teamId!),
    enabled: !!teamId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: options?.retry,
  });
};

export const usePipelineById = (
  pipelineId: string | null,
  options?: QueryOptions<GetPipelineByIdResponse>,
) => {
  return useQuery({
    queryKey: ['pipelines', 'by-id', pipelineId],
    queryFn: () => fetchPipelineById(pipelineId!),
    enabled: !!pipelineId && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: options?.retry,
  });
};

// React Query hooks for configuration versions
export const usePipelineConfigurationVersions = (
  params?: GetPipelineConfigurationVersionsRequest,
  options?: QueryOptions<GetPipelineConfigurationVersionsResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-configuration-versions', params],
    queryFn: () => fetchPipelineConfigurationVersions(params),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const usePipelineConfigurationVersionsByPipelineId = (
  pipelineId: string | null,
  options?: QueryOptions<GetPipelineConfigurationVersionsResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-configuration-versions', 'by-pipeline', pipelineId],
    queryFn: () => fetchPipelineConfigurationVersionsByPipelineId(pipelineId!),
    enabled: !!pipelineId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

// React Query hooks for executions
export const usePipelineExecutions = (
  params?: GetPipelineExecutionsRequest,
  options?: QueryOptions<GetPipelineExecutionsResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-executions', params],
    queryFn: () => fetchPipelineExecutions(params),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const usePipelineExecutionsByPipelineId = (
  pipelineId: string | null,
  options?: QueryOptions<GetPipelineExecutionsResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-executions', 'by-pipeline', pipelineId],
    queryFn: () => fetchPipelineExecutionsByPipelineId(pipelineId!),
    enabled: !!pipelineId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const usePipelineExecutionById = (
  pipelineExecutionId: string | null,
  options?: QueryOptions<GetPipelineExecutionByIdResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-executions', 'by-id', pipelineExecutionId],
    queryFn: () => fetchPipelineExecutionById(pipelineExecutionId!),
    enabled: !!pipelineExecutionId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const usePipelineExecutionOutput = (
  pipelineExecutionId: string | null,
  options?: QueryOptions<GetPipelineExecutionOutputResponse>,
) => {
  return useQuery({
    queryKey: ['pipeline-executions', 'output', pipelineExecutionId],
    queryFn: () => fetchPipelineExecutionOutput(pipelineExecutionId!),
    enabled: !!pipelineExecutionId && options?.enabled !== false,
    retry: options?.retry,
    staleTime: 5 * 60 * 1000, // 5 minutes - output doesn't change once execution is complete
  });
};

export const usePipelineConfigurationVersionById = (
  pipelineConfigurationVersionId: string | null,
  options?: QueryOptions<GetPipelineConfigurationVersionByIdResponse>,
) => {
  return useQuery({
    queryKey: [
      'pipeline-configuration-versions',
      'by-id',
      pipelineConfigurationVersionId,
    ],
    queryFn: () =>
      fetchPipelineConfigurationVersionById(pipelineConfigurationVersionId!),
    enabled: !!pipelineConfigurationVersionId && options?.enabled !== false,
    retry: options?.retry,
  });
};

// Mutation hooks for pipelines
export const useCreatePipeline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPipeline,
    onSuccess: () => {
      // Invalidate all pipeline queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });
};

export const useUpdatePipeline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pipelineId,
      data,
    }: {
      pipelineId: string;
      data: UpdatePipelineBody;
    }) => updatePipeline(pipelineId, data),
    onSuccess: () => {
      // Invalidate all pipeline queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-configuration-versions'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-executions'],
      });
    },
  });
};

export const useDeletePipeline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePipeline,
    onSuccess: (_, deletedPipelineId) => {
      // Invalidate all pipeline-related queries
      void queryClient.invalidateQueries({
        queryKey: ['pipelines'],
      });
      // Remove specific pipeline queries
      void queryClient.removeQueries({
        queryKey: ['pipelines', 'by-id', deletedPipelineId],
      });
      void queryClient.removeQueries({
        queryKey: [
          'pipeline-configuration-versions',
          'by-pipeline',
          deletedPipelineId,
        ],
      });
      void queryClient.removeQueries({
        queryKey: ['pipeline-executions', 'by-pipeline', deletedPipelineId],
      });
    },
  });
};

// Mutation hooks for configuration management
export const useCreatePipelineConfigurationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPipelineConfigurationVersion,
    onSuccess: () => {
      // Invalidate configuration version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-configuration-versions'],
      });
    },
  });
};

export const useUpdatePipelineConfigurationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pipelineConfigurationVersionId,
      data,
    }: {
      pipelineConfigurationVersionId: string;
      data: UpdatePipelineConfigurationVersionBody;
    }) =>
      updatePipelineConfigurationVersion(pipelineConfigurationVersionId, data),
    onSuccess: () => {
      // Invalidate configuration version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-configuration-versions'],
      });
    },
  });
};

export const useDeletePipelineConfigurationVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePipelineConfigurationVersion,
    onSuccess: () => {
      // Invalidate configuration version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-configuration-versions'],
      });
    },
  });
};

// Mutation hook for pipeline validation
export const usePipelineValidation = () => {
  return useMutation({
    mutationFn: validatePipelineConfiguration,
    // No cache invalidation needed for validation
  });
};

// Mutation hook for creating pipeline execution
export const useCreatePipelineExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPipelineExecution,
    onSuccess: (data) => {
      // Invalidate pipeline executions to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-executions'],
      });
      // Also invalidate the specific pipeline's executions
      if (data.pipelineConfigurationVersionId) {
        void queryClient.invalidateQueries({
          queryKey: ['pipeline-executions', 'by-pipeline'],
        });
      }
    },
  });
};

// Hierarchical block progress data interface
export interface HierarchicalBlockProgress {
  block_id: string;
  block_type: string;
  level: number;
  path: string[];
  parent_id: string | null;
  stage_name: string;
  start_time: number;
  end_time?: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  success?: boolean;
  error?: string | null;
  duration?: number;
  progress_messages?: Array<{
    timestamp: number;
    message: string;
    percentage: number;
    block_type: string;
  }>;
}

// Extended progress data with hierarchical support
export interface ExtendedPipelineExecutionProgressData
  extends PipelineExecutionProgressData {
  hierarchicalBlocks?: Record<string, HierarchicalBlockProgress>;
  progress_tracking?: {
    pipeline_status: string;
    total_blocks: number;
    completed_blocks: number;
    failed_blocks: number;
    start_time: number;
    progress_percentage: number;
    block_timings: Record<string, unknown>;
    block_progress: Record<string, unknown>;
  };
}

/**
 * Create default progress data structure for fallback scenarios
 */
const createDefaultProgressData =
  (): ExtendedPipelineExecutionProgressData => ({
    overallProgress: 0,
    totalBlocks: 0,
    completedBlocks: 0,
    blocks: {},
    hierarchicalBlocks: undefined,
    progress_tracking: undefined,
  });

/**
 * Transform raw Python progress data to TypeScript PipelineExecutionProgressData format
 */
const transformProgressData = (
  rawProgressData: Record<string, unknown>,
): ExtendedPipelineExecutionProgressData => {
  // Validate input data structure
  if (!rawProgressData || typeof rawProgressData !== 'object') {
    logger.warn('Invalid progress data structure, using defaults', {
      rawProgressData,
    });
    return createDefaultProgressData();
  }

  // Debug log raw data
  logger.debug('[transformProgressData] Raw progress data:', rawProgressData);

  const progressTracking = rawProgressData.progress_tracking;
  const hierarchicalBlocks = rawProgressData.hierarchical_blocks as
    | Record<string, HierarchicalBlockProgress>
    | undefined;

  // Handle progress_percentage with validation - expects 0-1 from Python, but handle legacy 0-100 values
  let overallProgress = 0;
  if (
    progressTracking &&
    typeof progressTracking === 'object' &&
    progressTracking !== null
  ) {
    const trackingData = progressTracking as Record<string, unknown>;
    if ('progress_percentage' in trackingData) {
      const rawProgress = trackingData.progress_percentage;
      if (typeof rawProgress === 'number' && !isNaN(rawProgress)) {
        overallProgress =
          rawProgress > 1
            ? Math.min(1, Math.max(0, rawProgress / 100)) // Legacy 0-100 values
            : Math.min(1, Math.max(0, rawProgress)); // New 0-1 values
      }
    }
  }

  // Extract block-level progress from hierarchical data if available
  const blocks: Record<string, unknown> = {};
  if (hierarchicalBlocks) {
    Object.entries(hierarchicalBlocks).forEach(([blockId, blockData]) => {
      // Convert hierarchical block to PipelineExecutionProgressData block format
      let progress = 0;
      if (blockData.status === 'completed') progress = 1;
      else if (blockData.status === 'failed') progress = 1;
      else if (
        blockData.progress_messages &&
        blockData.progress_messages.length > 0
      ) {
        // Get latest progress from messages - handle both 0-1 and 0-100 ranges
        const latestProgress =
          blockData.progress_messages[blockData.progress_messages.length - 1]
            .percentage;
        progress = latestProgress > 1 ? latestProgress / 100 : latestProgress;
      }

      blocks[blockId] = {
        status: blockData.status,
        progress: Math.min(1, Math.max(0, progress)),
        message:
          blockData.progress_messages?.[blockData.progress_messages.length - 1]
            ?.message || blockData.status,
        startedAt: blockData.start_time
          ? new Date(blockData.start_time * 1000).toISOString()
          : undefined,
        completedAt: blockData.end_time
          ? new Date(blockData.end_time * 1000).toISOString()
          : undefined,
        error: blockData.error || undefined,
        metadata: {
          level: blockData.level,
          path: blockData.path,
          parent_id: blockData.parent_id,
          stage_name: blockData.stage_name,
          block_type: blockData.block_type,
        },
      };
    });
  }

  // Extract totalBlocks and completedBlocks with proper type guards
  let totalBlocks = 0;
  let completedBlocks = 0;

  if (
    progressTracking &&
    typeof progressTracking === 'object' &&
    progressTracking !== null
  ) {
    const trackingData = progressTracking as Record<string, unknown>;

    if (
      'total_blocks' in trackingData &&
      typeof trackingData.total_blocks === 'number'
    ) {
      totalBlocks = trackingData.total_blocks;
    }

    if (
      'completed_blocks' in trackingData &&
      typeof trackingData.completed_blocks === 'number'
    ) {
      completedBlocks = trackingData.completed_blocks;
    }
  }

  return {
    overallProgress,
    totalBlocks,
    completedBlocks,
    blocks: blocks as Record<
      string,
      {
        message: string;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
        progress: number;
        startedAt?: string;
        completedAt?: string;
        metadata?: Record<string, unknown>;
        error?: string;
        subTasks?: Record<string, { status: string; progress: number }>;
      }
    >,
    currentBlockId: undefined, // Could be derived from running blocks
    hierarchicalBlocks,
    progress_tracking: progressTracking as
      | {
          pipeline_status: string;
          total_blocks: number;
          completed_blocks: number;
          failed_blocks: number;
          start_time: number;
          progress_percentage: number;
          block_timings: Record<string, unknown>;
          block_progress: Record<string, unknown>;
        }
      | undefined,
  };
};

export interface PipelineExecutionProgressHookResult {
  execution: GetPipelineExecutionByIdResponse | null;
  progressData: ExtendedPipelineExecutionProgressData;
  isLoading: boolean;
  isComplete: boolean;
  isRunning: boolean;
}

/**
 * Hook to subscribe to real-time pipeline execution progress updates via WebSocket
 */
export const usePipelineExecutionProgress = (
  executionId: string | null,
): PipelineExecutionProgressHookResult => {
  const [progressData, setProgressData] =
    useState<ExtendedPipelineExecutionProgressData | null>(null);
  const [invalidationTrigger, setInvalidationTrigger] = useState(0);
  const { subscribe, unsubscribe } = useWebsocketPubSub();
  const queryClient = useQueryClient();
  const executionIdRef = useRef(executionId);
  executionIdRef.current = executionId;

  // Debounce invalidation trigger to prevent API spam
  const [debouncedInvalidationTrigger] = useDebounce(invalidationTrigger, 1000);

  // Query for initial data and updates
  const { data: execution, isLoading } = usePipelineExecutionById(executionId);

  // Update progress data from execution query and transform from Python format
  useEffect(() => {
    if (execution?.progressData) {
      logger.info(
        '[usePipelineExecutionProgress] Received execution progress data',
        {
          executionId,
          status: execution.status,
          hasProgressData: !!execution.progressData,
        },
      );

      try {
        // Transform raw Python progress data to TypeScript schema format
        const rawProgressData = execution.progressData as Record<
          string,
          unknown
        >;
        const transformedData = transformProgressData(rawProgressData);
        setProgressData(transformedData);

        logger.info(
          '[usePipelineExecutionProgress] Transformed progress data',
          {
            executionId,
            overallProgress: transformedData.overallProgress,
            totalBlocks: transformedData.totalBlocks,
            completedBlocks: transformedData.completedBlocks,
          },
        );
      } catch (error) {
        logger.warn('Failed to transform progress data', {
          error,
          rawData: execution.progressData,
        });
        // Fallback to empty progress data with safe defaults
        setProgressData(createDefaultProgressData());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution?.progressData, executionId]);

  // Trigger debounced invalidation when trigger value changes
  useEffect(() => {
    if (debouncedInvalidationTrigger > 0 && executionIdRef.current) {
      void queryClient.invalidateQueries({
        queryKey: ['pipeline-executions', 'by-id', executionIdRef.current],
      });
    }
  }, [debouncedInvalidationTrigger, queryClient]);

  // Subscribe to WebSocket updates
  useEffect(() => {
    if (!executionId) return;

    const subscriptionId = subscribe(
      {
        topic: 'resource_updated',
        options: {
          resourceType: 'pipeline_execution',
          resourceId: executionId,
        },
      },
      (event) => {
        if (
          event.type === 'resource_updated' &&
          event.data.resourceId === executionId
        ) {
          logger.info(
            '[WebSocket] Pipeline execution update received, triggering invalidation',
            {
              executionId,
              resourceType: event.data.resourceType,
            },
          );

          // Always trigger throttled invalidation for pipeline execution updates
          // The debouncing (1000ms) prevents excessive API calls while still allowing progress updates
          setInvalidationTrigger((prev) => prev + 1);
        }
      },
    );

    return () => {
      unsubscribe(subscriptionId);
    };
    // NOTE: do not include subscribe/unsubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  return {
    execution: execution || null,
    progressData: progressData || createDefaultProgressData(),
    isLoading,
    isComplete:
      execution?.status === 'completed' || execution?.status === 'failed',
    isRunning:
      execution?.status === 'running' || execution?.status === 'pending',
  };
};
